import React, { useState, useEffect, useContext } from 'react';
import { withRouter, Route, Switch, Redirect } from 'react-router-dom';
import _ from 'lodash';
import { connect } from 'react-redux';
import GenericListPair from 'modules/common/components/GenericListPair';
import ConfirmAlert from 'modules/common/components/ConfirmAlert';
import useDebounce from 'modules/common/components/UseDebounce';
import { ModalContext } from 'App';
import PropTypes from 'prop-types';
import * as actions from '../action';
import * as c from '../constant';
import LandOwnerContent from './LandOwnerContent';
import OwnerModal from '../modals/OwnerModal';
import CreateCoOwnerModal from '../components/CreateCoOwnerModal';
import UpdateCoOwnerModal from '../components/UpdateCoOwnerModal';
import PropertyDetailsModal from '../components/PropertyDetailsModal';

const form = {
  keyword: '',
};

function SettingsContainer({
  match,
  history,
  getLandOwnerList,
  landOwnerList,
  isLoading,
  removeLandOwner,
  removeCoOwner,
  landOwnerListPager,
  getSingleLandOwner,
}) {
  const [formData, setFormData] = useState(form);
  const setModal = useContext(ModalContext);

  const handleOnChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleAddModal = (e) => {
    e.preventDefault();
    setModal({
      isOpen: true,
      content: <OwnerModal type="NEW" />,
      title: 'Add Owner',
      modalSize: 'modal-md',
    });
  };

  const handleEdit = (data) => (e) => {
    e.preventDefault();
    setModal({
      isOpen: true,
      content: (
        <OwnerModal type="EDIT" updateList={data.attributes} id={data.id} />
      ),
      title: 'Update Owner',
      modalSize: 'modal-md',
    });
  };

  const handleRemove = (data) => (e) => {
    e.preventDefault();
    ConfirmAlert(
      'Are you sure, you want to remove this transaction code?',
      () => {
        removeLandOwner(data.id, () => {
          getLandOwnerList();
          const id = _.get(landOwnerList, '0.id');
          history.push(`${id}`);
          getSingleLandOwner(id);
        });
      },
      () => {},
      {
        yesLabel: 'Confirm',
        noLabel: 'Cancel',
      }
    );
  };

  const handleAddCoOwnerModal = (id) => (e) => {
    e.preventDefault();
    setModal({
      isOpen: true,
      content: <CreateCoOwnerModal id={id} />,
      title: 'Add Co Owner',
      modalSize: 'modal-md',
    });
  };

  const handleCoOwnerEdit = (data) => (e) => {
    e.preventDefault();
    setModal({
      isOpen: true,
      content: <UpdateCoOwnerModal coOwner={data} />,
      title: 'Update Co Owner',
      modalSize: 'modal-md',
    });
  };

  const handleCoOwnerRemove = (data) => (e) => {
    e.preventDefault();
    ConfirmAlert(
      'Are you sure, you want to remove this Co Owner?',
      () => {
        removeCoOwner(data, () => {
          getLandOwnerList();
          history.push(`/rpt/land/land-owner/${data.rptax_land_owner_id}`);
          getSingleLandOwner(data.rptax_land_owner_id);
        });
      },
      () => {},
      {
        yesLabel: 'Confirm',
        noLabel: 'Cancel',
      }
    );
  };

  const handleViewPropDetails = (data) => (e) => {
    e.preventDefault();
    setModal({
      isOpen: true,
      content: <PropertyDetailsModal data={data.attributes} id={data.id} />,
      title: 'Property Details',
      modalSize: 'modal-lg',
    });
  };

  useEffect(() => {
    getLandOwnerList();
    return () => {
      setModal({ isOpen: false });
    };
  }, []);

  const debouncedSearchTerm = useDebounce(formData, 500);

  const param = {
    q: formData.keyword,
    sort_by: 'asc',
  };
  useEffect(() => {
    if (debouncedSearchTerm) {
      getLandOwnerList({ q: formData.keyword });
    } else {
      setFormData([]);
    }
  }, [debouncedSearchTerm]);

  return (
    <div className="card-content-listing">
      <Route
        path={`${match.path}/:landOwnerId?`}
        render={({ match: { params } }) => (
          <GenericListPair
            moduleName="Land Owners"
            getData={getLandOwnerList}
            setParams={param}
            setKeyword={handleOnChange}
            keyword={_.get(formData, 'keyword') || ''}
            keywordPlaceholder="Search..."
            pathName={`${match.path}/:landOwnerId?`}
            listName="landOwner"
            label="Transaction Codes"
            renderItem={(item) => (
              <div className="p-3">{_.get(item, 'attributes.name')}</div>
            )}
            isLoading={isLoading}
            selected={params.landOwnerId}
            data={landOwnerList}
            dataPager={landOwnerListPager}
            handleSelectRow={(data) => {
              history.push(`${match.url}/${_.get(data, 'id')}`);
            }}
            renderContent={() => (
              <React.Fragment>
                <Switch>
                  <Route
                    path={`${match.path}/:landOwnerId?`}
                    render={() => (
                      <LandOwnerContent
                        handleAddModal={handleAddModal}
                        handleEdit={handleEdit}
                        handleRemove={handleRemove}
                        handleAddCoOwnerModal={handleAddCoOwnerModal}
                        handleCoOwnerEdit={handleCoOwnerEdit}
                        handleCoOwnerRemove={handleCoOwnerRemove}
                        handleViewPropDetails={handleViewPropDetails}
                      />
                    )}
                  />
                </Switch>
                <Route
                  path={`${match.path}/:landOwnerId?`}
                  render={() =>
                    !params.landOwnerId && landOwnerList.length > 0 ? (
                      <Redirect
                        to={`${match.url}/${_.get(landOwnerList, '0.id')}`}
                      />
                    ) : null
                  }
                />
              </React.Fragment>
            )}
          />
        )}
      />
    </div>
  );
}

SettingsContainer.propTypes = {
  match: PropTypes.instanceOf(Object).isRequired,
  history: PropTypes.instanceOf(Object).isRequired,
  getLandOwnerList: PropTypes.instanceOf(Function).isRequired,
  landOwnerList: PropTypes.instanceOf(Array).isRequired,
  landOwnerListPager: PropTypes.instanceOf(Object).isRequired,
  removeLandOwner: PropTypes.instanceOf(Function).isRequired,
  removeCoOwner: PropTypes.instanceOf(Function).isRequired,
  isLoading: PropTypes.bool.isRequired,
  getSingleLandOwner: PropTypes.instanceOf(Function).isRequired,
};

const mapStateToProps = ({ api }) => ({
  landOwnerList: _.get(api, `${c.GET_LAND_OWNER}.list`) || [],
  landOwnerListPager: _.get(api, `${c.GET_LAND_OWNER}.pager`) || {},
  isLoading: _.get(api, 'loading', []).indexOf(c.GET_LAND_OWNER) > -1,
});

const enhance = _.flowRight([withRouter, connect(mapStateToProps, actions)]);

export default enhance(SettingsContainer);
